import { GridApi, GridReadyEvent } from '@ag-grid-enterprise/all-modules';
import { Box, makeStyles } from '@material-ui/core';
import { RowNode } from 'ag-grid-community';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import LoadingContainer from '../../../components/LoadingContainer';
import BenchmarkModal from '../../../components/Modals/benchmarkModal';
import TableGrid from '../../../components/TableGrid';
import {
  useTableGridApiDispatch,
  useTableGridApiState,
} from '../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { TableGridApiAction } from '../../../components/TableGrid/TableGridApiContext/TableGridApiContext.types';
import DataGridToolbar from '../../../components/TableGrid/TableGridToolbar';
import ActionBox from '../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox';
import { ActionLink } from '../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox.type';
import CountBox from '../../../components/TableGrid/TableGridToolbar/CountBox/CountBox';
import { api } from '../../../core/api';
import { cloneBenchmark, editBenchmark } from '../../../core/api/benchmark';
import { border } from '../../../core/utils/theme.utils';
import { BenchmarkView } from '../../../generated/model/models';
import { useErrorsDispatch } from '../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkDispatch, useBenchmarkState } from '../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../Context/BenchmarkContext.types';
import { getBenchmarkListTableColumns } from './BenchmarkListTableColumns';
import { getBenchwiseListTableColumns } from './BenchwiseListColumns';

const BenchmarkListTable: React.FC<{ benchwiseOnly?: boolean }> = ({ benchwiseOnly }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useBenchmarkDispatch();
  const { selectedRows, tableGridApi } = useTableGridApiState();
  const { activeBenchmark } = useBenchmarkState();
  const [showBenchmarkEditModal, setShowBenchmarkEditModal] = useState(false);
  const [showBenchmarkCloneModal, setShowBenchmarkCloneModal] = useState(false);
  const [data, setData] = useState<BenchmarkView[]>([]);
  const dispatchErrors = useErrorsDispatch();
  const dispatchTableApi = useTableGridApiDispatch();

  const onOpenEditModal = useMemo(
    () => (benchmarkRow: BenchmarkView) => {
      dispatch({
        type: BenchmarkContextActionTypes.SetActiveBenchmark,
        payload: benchmarkRow,
      });
      setShowBenchmarkEditModal(true);
    },
    [dispatch],
  );

  const onOpenCloneModal = useMemo(
    () => (benchmarkRow: BenchmarkView) => {
      dispatch({
        type: BenchmarkContextActionTypes.SetActiveBenchmark,
        payload: benchmarkRow,
      });
      setShowBenchmarkCloneModal(true);
    },
    [dispatch],
  );

  const columns = useMemo(
    () =>
      benchwiseOnly
        ? getBenchwiseListTableColumns(t, onOpenEditModal, onOpenCloneModal)
        : getBenchmarkListTableColumns(t, onOpenEditModal, onOpenCloneModal),
    [t, benchwiseOnly, onOpenEditModal, onOpenCloneModal],
  );

  const onGridReady = async (params: GridReadyEvent) => {
    await fetchData(params.api);
  };

  const fetchData = async (gridApi: GridApi) => {
    gridApi.showLoadingOverlay();
    try {
      const result: BenchmarkView[] = await api.benchmark.fetchBenchmarks(benchwiseOnly);
      setData(result);
      if (result.length > 0) {
        gridApi.hideOverlay();
      } else {
        gridApi.showNoRowsOverlay();
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      setData([]);
      gridApi.showNoRowsOverlay();
    }
  };

  const refreshTable = async () => {
    await fetchData(tableGridApi);
    dispatchTableApi({ type: TableGridApiAction.ResetSelectedRows });
  };

  function onCloseBenchmarkModal() {
    setShowBenchmarkEditModal(false);
    setShowBenchmarkCloneModal(false);
  }

  function onSaveBenchmarkEditModal() {
    onCloseBenchmarkModal();
    refreshTable();
  }

  const actionItems = getActionItems(tableGridApi, selectedRows, refreshTable);

  return (
    <Box className={classNames([classes.root])}>
      <TableGrid
        tableToolbar={
          <DataGridToolbar>
            <CountBox
              boxTitle={'benchmark:savedBenchmarks'}
              totalCount={data.length}
              selectedCount={selectedRows.length}
            />
            <ActionBox buttonTitle={t('app:action')} actionItems={actionItems} />
          </DataGridToolbar>
        }
        onGridReady={onGridReady}
        columns={columns}
        isShowSideBar={false}
        suppressTableContextProvider={true}
        rowSelection={true}
        rowSelectionIdGetter={rowSelectionIdGetter}
        originalProps={{
          rowModelType: 'clientSide',
          rowData: data,
          suppressRowClickSelection: true,
          rowSelection: 'multiple',
          overlayLoadingTemplate: renderToStaticMarkup(<LoadingContainer />),
          defaultColDef: {
            sortable: false,
            resizable: true,
            flex: 1,
            menuTabs: [],
            minWidth: 100,
          },
        }}
      />
      <BenchmarkModal
        show={showBenchmarkEditModal || showBenchmarkCloneModal}
        title={
          showBenchmarkEditModal
            ? t('benchmark:benchmarkList.editModalTitle')
            : t('benchmark:benchmarkList.cloneModalTitle')
        }
        defaultValue={
          showBenchmarkEditModal ? activeBenchmark?.name : `${activeBenchmark?.name} clone`
        }
        handleSave={onSaveBenchmarkEditModal}
        handleClose={onCloseBenchmarkModal}
        submitAction={showBenchmarkEditModal ? editBenchmark : cloneBenchmark}
      />
    </Box>
  );
};

const rowSelectionIdGetter = (node: RowNode) => {
  return node.data?.id;
};

const getActionItems = (
  tableGridApi: GridApi,
  selectedRows: string[],
  refreshTable: () => void,
): ActionLink[] => [
  {
    title: 'queryList:deleteSelectedItems',
    onClick: async () => {
      tableGridApi.showLoadingOverlay();
      ReactGA.event({ category: 'BENCHMARK', action: 'DELETE_SELECTED_CLICK' });
      await api.benchmark.deleteSelectedBenchmarks(selectedRows, refreshTable);
    },
    count: selectedRows.length,
    isNotActive: false,
    isHidden: false,
  },
  {
    title: 'benchmark:action.share',
    onClick: async () => {
      tableGridApi.showLoadingOverlay();
      ReactGA.event({ category: 'BENCHMARK', action: 'SHARE_SELECTED_CLICK' });
      await api.benchmark.shareBenchmarks(selectedRows, true, refreshTable);
    },
    count: selectedRows.length,
    isNotActive: false,
    isHidden: false,
  },
  {
    title: 'benchmark:action.unshare',
    onClick: async () => {
      tableGridApi.showLoadingOverlay();
      ReactGA.event({ category: 'BENCHMARK', action: 'UNSHARE_SELECTED_CLICK' });
      await api.benchmark.shareBenchmarks(selectedRows, false, refreshTable);
    },
    count: selectedRows.length,
    isNotActive: false,
    isHidden: false,
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    borderLeft: border(theme.palette.divider),
    borderRight: border(theme.palette.divider),
    overflow: 'hidden',

    '& .ag-theme-alpine': {
      borderLeft: 0,
      borderRight: 0,
    },
  },
}));

export default BenchmarkListTable;
